import React, { useEffect, useCallback } from 'react';
import { parseEmailContent } from '../../utils/emailParser';

const EmailViewModal = ({ isOpen, onClose, email, onReply }) => {
  // Handle ESC key press to close the modal
  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  
  // Add and remove event listener for ESC key
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    
    // Clean up event listener when component unmounts or modal closes
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleEscapeKey]);
  
  if (!isOpen || !email) return null;

  return (
    <div className="fixed inset-0 overflow-y-auto z-50" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
             aria-hidden="true" 
             onClick={onClose}></div>
        
        <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-2xl">
          <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Email Details
                </h3>
                
                <div className="mt-4 bg-gray-50 p-4 rounded border border-gray-200">
                  <dl>
                    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-200">
                      <dt className="text-sm font-medium text-gray-500">From:</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{email.sender_name || 'Unknown'}</dd>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-200">
                      <dt className="text-sm font-medium text-gray-500">To:</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{email.recipient}</dd>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-200">
                      <dt className="text-sm font-medium text-gray-500">Date:</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        {new Date(email.created_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </dd>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-200">
                      <dt className="text-sm font-medium text-gray-500">Subject:</dt>
                      <dd className="text-sm text-gray-900 col-span-2 font-semibold">{email.subject}</dd>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 py-2">
                      <dt className="text-sm font-medium text-gray-500">Message ID:</dt>
                      <dd className="text-sm text-gray-900 col-span-2 break-all">
                        <span className="text-xs font-mono">{email.message_id || 'Not available'}</span>
                      </dd>
                    </div>
                  </dl>
                </div>
                
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Message Content:</h4>
                  {email.message_body ? (
                    <div className="bg-white p-4 rounded border border-gray-200 max-h-96 overflow-y-auto">
                      {(() => {
                        const { content, isHtml } = parseEmailContent(email.message_body);
                        
                        if (!content) {
                          return (
                            <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
                              <div className="flex items-center">
                                <p className="text-sm text-yellow-700">
                                  Could not parse email content.
                                </p>
                              </div>
                            </div>
                          );
                        }
                        
                        if (isHtml) {
                          return (
                            <div 
                              className="prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: content }} 
                            />
                          );
                        } else {
                          return (
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">
                              {content}
                            </p>
                          );
                        }
                      })()}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-yellow-700">
                          The message content is not available for this email. This may be because it was sent before the system was updated to store message content.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {/* Reply button - only show for incoming emails and if onReply handler is provided */}
            {onReply && email?.direction === 'incoming' && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => onReply(email)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Reply
              </button>
            )}
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailViewModal;